import http, { endpoints } from '../http';
import {
  ILinkMaterialDto,
  IDocumentMaterialDto,
  IBatchItemDto,
  ISimplifiedVisualMaterialDto,
  IAllowedMaterialNameDto,
} from './dtos';
import {
  mapLinkOrDocumentMaterialFromDto,
  mapLinkOrDocumentMaterialToDto,
  mapSimplifiedVisualMaterialFromDto,
} from './helpers';
import {
  ISendDownloadInfoParams,
  IUpdateVisualMaterialsParams,
  ICopyMaterialsParams,
  ILinkMaterial,
  IDocumentMaterial,
  ISealBatchUploadedMaterialParams,
  IUploadedMaterial,
  IRegisterBatchMaterialParams,
  IRemoveBatchMaterialParams,
  IMaterialBatchRemoveParams,
  IRemoveLinkOrDocumentMaterialParams,
  ILinkOrDocumentMaterialParams,
  ICollageSubmitParams,
} from './models';
import { IDatahubCase } from '../cases/models';
import { IDatahubOrderLine } from '../orders/models';
import { LocalizedError } from 'localization';
import { ICommonResponse } from '../types';
import { ENDPOINT_DETAILS } from 'constants/server/apiEndPoint';

const datahubClient = http.createClient(endpoints.datahub);

export default class MaterialApi {
  static async sendDownloadInfo(params: ISendDownloadInfoParams) {
    const response = await datahubClient.post<ICommonResponse>('/Assets/download-link/ewarp-materials/email', params);

    if (!response?.data?.success) {
      throw new LocalizedError('materials.messages.packageZipFileFailed');
    }

    return response.data;
  }

  static async archive(caseId: IDatahubCase['id']) {
    const response = await datahubClient.post<ICommonResponse>(`/cases/${caseId}/assets/archive`);
    return response?.data;
  }

  static async getSimplifiedVisualMaterials(
    id: IDatahubCase['id'],
    options?: {
      signal?: AbortSignal;
    }
  ) {
    const response = await datahubClient.get<{
      batchProductInfos: IBatchItemDto[];
      links: {
        linkType: 'interactiveFloorPlanEditor' | 'hotspotEditor' | 'hotspotZip'; // TODO: add panoramas
        url: string;
      }[];
      materials: ISimplifiedVisualMaterialDto[];
    }>(`/cases/${id}/assets`, {
      signal: options?.signal,
    });

    return {
      batchList: response?.data?.batchProductInfos || [],
      links: response?.data?.links || [],
      materials: response?.data?.materials.map(mapSimplifiedVisualMaterialFromDto) || [],
    };
  }

  static async updateMaterials({ caseId, ...params }: IUpdateVisualMaterialsParams) {
    const response = await datahubClient.post<ICommonResponse>(`/cases/${caseId}/assets/sortorder`, params);
    return response?.data;
  }

  static async copyMaterials({ caseId, ...params }: ICopyMaterialsParams) {
    const response = await datahubClient.post<ICommonResponse>(`/cases/${caseId}/assets/copy`, {
      ...params,
      copyAll: params.copyAll,
      copyCopyWriteTexts: false,
      copyDocuments: false,
      copyLinks: false,
    });

    return response?.data;
  }

  static async getLinkMaterials(caseId: IDatahubCase['id']) {
    const response = await datahubClient.get<ILinkMaterialDto[]>(`/cases/${caseId}/assets/links`);
    return response?.data?.map<ILinkMaterial>(mapLinkOrDocumentMaterialFromDto) || [];
  }

  static async getDocumentMaterials(caseId: IDatahubCase['id'], options?: { signal?: AbortSignal }) {
    const response = await datahubClient.get<IDocumentMaterialDto[]>(
      ENDPOINT_DETAILS.getAssetDocumentsByCaseId(caseId),
      {
        signal: options?.signal,
      }
    );
    return response?.data?.map<IDocumentMaterial>(mapLinkOrDocumentMaterialFromDto) || [];
  }

  static async saveLinkAsset({ caseId, assets }: ILinkOrDocumentMaterialParams) {
    const response = await datahubClient.post<ICommonResponse>(
      `/cases/${caseId}/assets/link`,
      assets.map(mapLinkOrDocumentMaterialToDto)
    );
    return response?.data;
  }

  static async saveDocumentAsset({ caseId, assets }: ILinkOrDocumentMaterialParams) {
    const response = await datahubClient.post<ICommonResponse>(
      `/cases/${caseId}/assets/document`,
      assets.map(mapLinkOrDocumentMaterialToDto)
    );
    return response?.data;
  }

  static async deleteLinkAsset({ caseId, ...params }: IRemoveLinkOrDocumentMaterialParams) {
    const response = await datahubClient.post<ICommonResponse>(`/cases/${caseId}/assets/delete`, params);
    return response?.data;
  }

  static async getUploadedMaterialForOrderLine(orderlineId: IDatahubOrderLine['id']) {
    const response = await datahubClient.get<IUploadedMaterial[]>(`/orders/orderlines/${orderlineId}/taskfileuploads`);

    return response?.data || [];
  }

  static async sealBatchUploadedMaterial({ orderlineId, uploads }: ISealBatchUploadedMaterialParams) {
    const response = await datahubClient.post<ICommonResponse>(
      `/orders/orderlines/${orderlineId}/materials/add`,
      uploads
    );

    return response?.data;
  }

  static async registerBatchMaterial({ orderlineId, ...params }: IRegisterBatchMaterialParams) {
    const response = await datahubClient.post<ICommonResponse>(
      `/orders/orderlines/${orderlineId}/taskfileuploads/add`,
      params
    );

    return response?.data;
  }

  static async removeBatchMaterial({ materialId }: IRemoveBatchMaterialParams) {
    const response = await datahubClient.delete<ICommonResponse>(`/orders/orderlines/taskfileuploads/${materialId}`);
    return response?.data;
  }

  static async getSuggestedMaterialNames(caseId: IDatahubCase['id']) {
    const response = await datahubClient.get<IAllowedMaterialNameDto[]>(`/cases/${caseId}/assets/allowednames`);

    return response?.data?.map(({ value }) => value) || [];
  }

  static async recreateMaterials(caseId: IDatahubCase['id']) {
    const response = await datahubClient.post<ICommonResponse>(`/cases/${caseId}/assets/restore`);
    return response?.data;
  }

  static async materialBatchRemove(params: IMaterialBatchRemoveParams) {
    const response = await datahubClient.delete<ICommonResponse>(
      `/cases/${params.caseId}/assets/batches/${params.batchId}`
    );

    return response?.data;
  }

  static async getCollagePrice(caseId: IDatahubCase['id']) {
    const response = await datahubClient.get<{
      featureEnabled: boolean;
      price: number;
      currency: string;
    }>(`/cases/${caseId}/assets/collage/pricing`);
    return response?.data;
  }

  static async submitCollage({ caseId, ...params }: ICollageSubmitParams) {
    const response = await datahubClient.post<ICommonResponse>(`/cases/${caseId}/assets/collage`, params);
    return response?.data;
  }

  static async deleteMaterialsAndAssets(orderLineIds: IDatahubOrderLine['id'][]) {
    const response = await datahubClient.delete<ICommonResponse>('/Assets/delete-materials-and-assets', {
      data: orderLineIds,
    });
    return response?.data;
  }
}
